import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Loader2, TrendingUp, Percent, ArrowDownToLine, Crown, CheckCircle2, XCircle, Clock, Receipt, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { MKT_GOLD, MKT_TINT } from "../../lib/marketplace";
import { apiComisiones, apiLedger, apiSolicitarRetiro, apiMisRetiros, apiGetConfiguracion } from "../../lib/marketplaceApi";
import { humanizeAxiosError } from "../../lib/api";

const soles = (n) => `S/ ${Number(n || 0).toFixed(2)}`;
const fecha = (s) => (s ? new Date(s).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const LEDGER_LABEL = {
  venta: { t: "Venta aprobada", c: "#16A34A" },
  comision: { t: "Comisión ConstruLink", c: "#DC2626" },
  reembolso: { t: "Reembolso", c: "#DC2626" },
  ajuste: { t: "Ajuste administrativo", c: "#2563EB" },
  retiro_solicitado: { t: "Retiro solicitado", c: "#CA8A04" },
  retiro_aprobado: { t: "Retiro aprobado", c: "#2563EB" },
  retiro_pagado: { t: "Retiro pagado", c: "#16A34A" },
  retiro_rechazado: { t: "Retiro rechazado", c: "#64748B" },
};
const ESTADO_RETIRO = {
  solicitado: { t: "Solicitado", c: "#CA8A04", Icon: Clock },
  aprobado: { t: "Aprobado", c: "#2563EB", Icon: CheckCircle2 },
  pagado: { t: "Pagado", c: "#16A34A", Icon: CheckCircle2 },
  rechazado: { t: "Rechazado", c: "#DC2626", Icon: XCircle },
};

function KPI({ Icon, label, value, color }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
      <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold"><Icon size={15} style={{ color }} /> {label}</div>
      <p className="mt-1.5 text-xl font-extrabold text-slate-900">{value}</p>
    </div>
  );
}

export default function ComisionesVendedor() {
  const [saldo, setSaldo] = useState(undefined);
  const [movs, setMovs] = useState([]);
  const [retiros, setRetiros] = useState([]);
  const [monto, setMonto] = useState("");
  const [datos, setDatos] = useState("");
  const [metodo, setMetodo] = useState("transferencia");
  const [enviando, setEnviando] = useState(false);

  const cargar = async () => {
    try {
      const [s, l, r] = await Promise.all([apiComisiones(), apiLedger(), apiMisRetiros()]);
      setSaldo(s); setMovs(l.items || []); setRetiros(r.items || []);
    } catch { setSaldo(null); }
    try {
      const cfg = await apiGetConfiguracion();
      if (cfg?.payout_metodo) setMetodo(cfg.payout_metodo);
      if (cfg?.payout_datos) setDatos((d) => d || cfg.payout_datos);
    } catch { /* sin config aún */ }
  };
  useEffect(() => { cargar(); }, []);

  const solicitar = async (e) => {
    e.preventDefault();
    const m = parseFloat(monto);
    if (!m || m <= 0) { toast.error("Ingresa un monto válido"); return; }
    setEnviando(true);
    try {
      await apiSolicitarRetiro(m, metodo, datos);
      toast.success("Solicitud de retiro enviada. El administrador la revisará.");
      setMonto("");
      await cargar();
    } catch (err) { toast.error(humanizeAxiosError(err)); }
    finally { setEnviando(false); }
  };

  if (saldo === undefined) return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="comisiones-loading"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  if (!saldo) return <p className="text-slate-400 py-10">No se pudo cargar la información de comisiones.</p>;

  return (
    <div className="space-y-6" data-testid="comisiones-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2"><Wallet size={24} style={{ color: MKT_GOLD }} /> Comisiones y saldo</h1>
          <p className="text-sm text-slate-500 mt-0.5">Ingresos, comisiones y retiros de tu tienda.</p>
        </div>
        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold" style={{ backgroundColor: MKT_TINT, color: MKT_GOLD }} data-testid="comisiones-plan">
          <Crown size={14} /> Plan {saldo.plan_label} · Comisión {saldo.comision_pct}%
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI Icon={TrendingUp} label="Ventas aprobadas" value={saldo.ventas_aprobadas} color="#16A34A" />
        <KPI Icon={DollarSign} label="Ingresos brutos" value={soles(saldo.ingresos_brutos)} color="#2563EB" />
        <KPI Icon={Percent} label="Comisión pagada" value={soles(saldo.comision_total)} color="#DC2626" />
        <KPI Icon={Wallet} label="Ganancia neta" value={soles(saldo.ganancia_neta)} color={MKT_GOLD} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KPI Icon={Clock} label="Saldo pendiente (en retiro)" value={soles(saldo.saldo_pendiente)} color="#CA8A04" />
        <KPI Icon={ArrowDownToLine} label="Disponible para retiro" value={soles(saldo.saldo_disponible)} color="#16A34A" />
        <KPI Icon={CheckCircle2} label="Total retirado" value={soles(saldo.retirado)} color="#64748B" />
      </div>

      {/* Solicitar retiro */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="retiro-form">
        <h2 className="font-bold text-slate-900 mb-1 inline-flex items-center gap-2"><ArrowDownToLine size={18} style={{ color: MKT_GOLD }} /> Solicitar retiro</h2>
        <p className="text-xs text-slate-500 mb-3">Disponible: <b>{soles(saldo.saldo_disponible)}</b>. El administrador revisa y registra el pago manualmente.</p>
        <form onSubmit={solicitar} className="flex flex-col sm:flex-row gap-3 items-start">
          <input type="number" step="0.01" min="0" value={monto} onChange={(e) => setMonto(e.target.value)} placeholder="Monto (S/)" data-testid="retiro-monto" className="rounded-lg border border-slate-300 px-3 py-2 text-sm w-full sm:w-40 outline-none focus:border-amber-500" />
          <input value={datos} onChange={(e) => setDatos(e.target.value)} placeholder="Cuenta / datos de pago (banco, N° cuenta, CCI)" data-testid="retiro-datos" className="rounded-lg border border-slate-300 px-3 py-2 text-sm flex-1 w-full outline-none focus:border-amber-500" />
          <button type="submit" disabled={enviando} data-testid="retiro-enviar" className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-white font-bold text-sm hover:brightness-110 disabled:opacity-60" style={{ backgroundColor: MKT_GOLD }}>
            {enviando ? <Loader2 size={15} className="animate-spin" /> : <ArrowDownToLine size={15} />} Solicitar
          </button>
        </form>
      </section>

      {/* Historial de retiros */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="retiros-historial">
        <h2 className="font-bold text-slate-900 mb-3">Historial de retiros</h2>
        {retiros.length === 0 ? <p className="text-sm text-slate-400 py-2">Aún no has solicitado retiros.</p> : (
          <div className="space-y-2">
            {retiros.map((r) => {
              const st = ESTADO_RETIRO[r.estado] || ESTADO_RETIRO.solicitado;
              return (
                <div key={r.id} className="flex items-center justify-between border-b border-slate-50 last:border-0 py-2 text-sm">
                  <div><p className="font-semibold text-slate-800">{soles(r.monto)}</p><p className="text-xs text-slate-400">{fecha(r.created_at)} {r.motivo_rechazo ? `· ${r.motivo_rechazo}` : ""}</p></div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-full" style={{ color: st.c, backgroundColor: `${st.c}15` }}><st.Icon size={13} /> {st.t}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Libro contable */}
      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="ledger-historial">
        <h2 className="font-bold text-slate-900 mb-3 inline-flex items-center gap-2"><Receipt size={18} style={{ color: MKT_GOLD }} /> Movimientos</h2>
        {movs.length === 0 ? <p className="text-sm text-slate-400 py-2">Sin movimientos todavía.</p> : (
          <div className="space-y-1.5">
            {movs.map((m) => {
              const lb = LEDGER_LABEL[m.tipo] || { t: m.tipo, c: "#64748B" };
              return (
                <div key={m.id} className="flex items-center justify-between border-b border-slate-50 last:border-0 py-2 text-sm" data-testid={`ledger-${m.tipo}`}>
                  <div className="min-w-0"><span className="font-semibold" style={{ color: lb.c }}>{lb.t}</span><p className="text-xs text-slate-400 truncate">{m.descripcion} · {fecha(m.created_at)}</p></div>
                  <span className="font-bold flex-shrink-0" style={{ color: m.monto >= 0 ? "#16A34A" : "#DC2626" }}>{m.monto >= 0 ? "+" : ""}{soles(m.monto)}</span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <p className="text-xs text-slate-400">Los pagos a vendedores se realizan manualmente tras la aprobación administrativa. <Link to="/planes" className="font-semibold" style={{ color: MKT_GOLD }}>Mejora tu plan</Link> para reducir tu comisión.</p>
    </div>
  );
}
